import React, { useState } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import Change from '../Change';
import { changeDirection, fmt, fmtChangeFixed, fmtCount } from '../../utils/formatters';
import { formatRange } from '../../utils/dataEnds';
import {
  CONDITION_DAYS, CONDITION_NAMES, CONDITION_ONE, ConditionCard, GROUP_SHORT, Pattern, conditionGlyph, dayLabel,
  moneyRangeWords, rangeText,
} from '../../utils/weather';
import DotPlot from './DotPlot';
import GroupBars from './GroupBars';
import HourlyChart from './HourlyChart';
import WeatherGlyph from './WeatherGlyph';
import { FOCUS, NOTE } from './weatherUi';

// One card per condition with a shown pattern: the figure on the left, the
// proof chart in the middle, the categories on the right, and the last three
// times underneath.

const DAY_MS = 86_400_000;
const EXAMPLE_LEAD = ['Last', 'The time before', 'And before that'];

function spansAYear(p: Pattern): boolean {
  return Date.parse(p.window.end) - Date.parse(p.window.start) >= 350 * DAY_MS;
}

function Figure({ p }: { p: Pattern }) {
  const dir = changeDirection(p.effect * 100);
  const tone = dir === 'down' ? 'text-[#B54A4A]' : dir === 'up' ? 'text-[#2E6B4F]' : 'text-[#2D2A2E]';
  const Icon = dir === 'down' ? TrendingDown : TrendingUp;
  const days = CONDITION_DAYS[p.condition] ?? 'days';
  const [lo, hi] = [Math.min(p.low, p.high), Math.max(p.low, p.high)];
  return (
    <div>
      <div data-figure className={`flex items-center gap-1.5 font-sans text-[1.5rem] font-bold leading-[1.2] tabular-nums ${tone}`}>
        {dir !== 'flat' && <Icon size={22} strokeWidth={1.75} aria-hidden="true" />}
        <span className="sr-only">{dir === 'down' ? 'Down ' : 'Up '}</span>
        {fmtChangeFixed(p.effect * 100)}
      </div>
      <p className="mt-0.5 font-sans text-sm tabular-nums text-[#6B6670]">{rangeText(lo, hi)}</p>
      {p.dollars_low != null && p.dollars_high != null && (
        <p className="mt-3 font-sans text-sm tabular-nums text-[#2D2A2E]">
          About {moneyRangeWords(p.dollars_low, p.dollars_high)} {p.effect < 0 ? 'less' : 'more'}{p.group !== 'store' && ` in ${(GROUP_SHORT[p.group] ?? p.group).toLowerCase()}`} on a usual day
        </p>
      )}
      <p className={`${NOTE} mt-2 tabular-nums`}>
        {/* n_days counts clean days only (engine/weather/stats.py paired_diffs): that weather and no
            other, not a holiday, with ordinary days before it to compare against. */}
        Built from {fmtCount(p.n_days)} {days} {spansAYear(p) ? 'in the last year' : `from ${formatRange(p.window.start, p.window.end)}`} with no other weather that day.
      </p>
      <p className={`${NOTE} tabular-nums`}>Held up on {formatRange(p.holdout.window_start, p.holdout.window_end)}</p>
    </div>
  );
}

function Examples({ p }: { p: Pattern }) {
  const rows = (p.examples || []).slice(0, 3);
  if (!rows.length) return null;
  const one = CONDITION_ONE[p.condition] ?? 'day like this';
  return (
    <ul className="mt-5 divide-y divide-[#E6E2DE] border-t border-[#E6E2DE]">
      {rows.map((e, i) => (
        <li key={e.date} data-example className="flex items-baseline justify-between gap-3 py-2">
          <span className="min-w-0 font-sans text-sm tabular-nums text-[#2D2A2E]">
            <span className="text-[#6B6670]">{i === 0 ? `${EXAMPLE_LEAD[0]} ${one}: ` : `${EXAMPLE_LEAD[i]}: `}</span>
            {dayLabel(e.date)}, {fmt(e.actual)} against a usual {fmt(e.usual)}.
          </span>
          {e.usual > 0 && <Change value={(e.actual / e.usual - 1) * 100} size="sm" fixed className="shrink-0" />}
        </li>
      ))}
    </ul>
  );
}

type View = 'days' | 'hours';

/** The middle of the card: the dot plot, and the hours as a second tab when there are any. */
function Proof({ condition, p }: { condition: string; p: Pattern }) {
  const [view, setView] = useState<View>('days');
  const days = p.points?.length && p.ordinary ? <DotPlot condition={condition} points={p.points} ordinary={p.ordinary} group={p.group} /> : null;
  const hours = p.hourly ? <HourlyChart condition={condition} hourly={p.hourly} /> : null;
  if (!days || !hours) return days ?? hours;
  const tab = (v: View, label: string) => (
    <button
      type="button" role="tab" id={`tab-${condition}-${v}`} aria-selected={view === v} aria-controls={`panel-${condition}`}
      onClick={() => setView(v)}
      className={`rounded-[6px] px-3 py-1.5 font-sans text-xs font-medium transition-colors ${view === v ? 'bg-[#F0EBF5] text-[#4A2D5E]' : 'text-[#6B6670] hover:text-[#2D2A2E]'} ${FOCUS}`}
    >
      {label}
    </button>
  );
  return (
    <div>
      <div role="tablist" aria-label="Show the proof by" className="mb-3 inline-flex rounded-[8px] border border-[#E6E2DE] p-0.5">
        {tab('days', 'Days')}
        {tab('hours', 'Hours')}
      </div>
      <div role="tabpanel" id={`panel-${condition}`} aria-labelledby={`tab-${condition}-${view}`}>
        {view === 'days' ? days : hours}
      </div>
    </div>
  );
}

export default function PatternCard({ card }: { card: ConditionCard }) {
  const p = card.lead;
  const name = CONDITION_NAMES[card.condition] ?? card.condition;
  const chart = (p.points?.length && p.ordinary) || p.hourly ? <Proof condition={card.condition} p={p} /> : null;
  return (
    <article aria-labelledby={`pattern-${card.condition}`} className="rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card">
      <header className="flex items-center gap-2">
        <span className="text-[#4A2D5E]"><WeatherGlyph kind={conditionGlyph(card.condition)} /></span>
        <h3 id={`pattern-${card.condition}`} className="font-serif text-lg text-[#2D2A2E]">{name}</h3>
        {p.group !== 'store' && <span className="font-sans text-xs font-medium text-[#6B6670]">· {GROUP_SHORT[p.group] ?? p.group}</span>}
      </header>
      <div className="mt-4 grid gap-6 md:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] lg:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_minmax(0,15rem)]">
        <Figure p={p} />
        {chart && <div className="min-w-0">{chart}</div>}
        <div className={`min-w-0 ${chart ? 'md:col-span-2 lg:col-span-1' : ''}`}>
          <GroupBars condition={card.condition} patterns={card.patterns} notYet={card.notYet} lead={p.group} />
        </div>
      </div>
      <Examples p={p} />
    </article>
  );
}
